// Import utils
import { flattenObjectsByKey } from '@automator/shared/utils/flatten-objects-by-key';

import getItemsActionsFlowPath from '../get-items-actions-flow-path';

/**
 * Retrieves all action items, optionally filtering by a specified action ID to get the flow path.
 *
 * @param {Object} [params={}] - The parameters for filtering actions. Defaults to an empty object if not provided.
 * @param {Number} [params.beforeActionID] - Optional ID of the action before which to retrieve the flow path.
 * @param {Number[]} [params.exclude=[]] - Array of IDs to exclude from the results.
 * @param {Boolean} [params.includeClosure=true] - Whether to include closure items in the results. Closures will not exist in the future, and redirects will be converted to actions. This parameter allows for including closures as an exception for now.
 * @return {Object[]} An array of action items.
 */
const getItemsActions = ( { beforeActionID, exclude = [], includeClosure = true } = {} ) => {
	// Get all the items from the main object, and clone it to avoid mutation
	let actions = structuredClone( UncannyAutomator._recipe.actions.items );

	// If a beforeActionID is provided, get the flow path of items before this action.
	if ( beforeActionID ) {
		actions = getItemsActionsFlowPath( beforeActionID, actions );
	}

	// Flatten all nested items into a single array.
	actions = flattenObjectsByKey( actions, 'items' );

	// Filter out only the action items and exclude specified IDs.
	actions = actions.filter( action => {
		// Check if the action is an action or a closure, and if it's not excluded
		const isAction = action.type === 'action';
		const isClosure = includeClosure && action.type === 'closure';
		const isNotExcluded = ! exclude.includes( action.id );

		return ( isAction || isClosure ) && isNotExcluded;
	} );

	return actions;
}

export default getItemsActions;
